import React, { SVGAttributes, useState } from "react";

import {
  calculateTickByMacroScore,
  macroScoreRanges,
  spectralColors,
} from "../../utils/ticks";
import * as SC from "./Gauge.styles";

// Config
const canvas = 140; // Main value, the rest are relative to this one
const strokeWidth = 10; // Gauge stroke
const strokeDegrees = 270; // Gauge degrees
const gradientWidth = 4; // Percentae of gradient per tick
const tickWidth = 3; // Width of ticks
// End Config

const strokeSelector = strokeWidth;
const maxMacroScore = macroScoreRanges[macroScoreRanges.length - 1][1];
const adjustBase = (((360 - strokeDegrees) / 360) * 100) / 2;
const adjustPercentage = (percentage: number) =>
  percentage * (strokeDegrees / 360) + adjustBase;

const GaugeSvgGradient = React.memo(
  (() => {
    // const colorStepsGradient = macroScoreRanges
    //   .map(([low, high], i) => ([
    //     [(low / maxMacroScore * 100) + (gradientWidth / 2), macroScoreColors[i]],
    //     [(high / maxMacroScore * 100) - (gradientWidth / 2), macroScoreColors[i]],
    //   ]))
    //   .flat()
    //   .map(([position, color]) => `${color} ${adjustPercentage(+position)}%`)
    //   .join(', ')
    const colorStepsGradient = spectralColors
      .map((_, i, { length }) => [(i / length) * 100, ((i + 1) / length) * 100])
      .map(([low, high], i) => [
        [low + gradientWidth / 2, spectralColors[i]],
        [high - gradientWidth / 2, spectralColors[i]],
      ])
      .flat()
      .map(([position, color]) => `${color} ${adjustPercentage(+position)}%`)
      .join(", ");
    return (props: { mask?: string }) => {
      return (
        <g {...props}>
          <foreignObject width={canvas} height={canvas}>
            <SC.GradientContainer
              style={{
                width: canvas * 2,
                height: canvas * 2,
                background: `conic-gradient(${colorStepsGradient})`,
              }}
            />
          </foreignObject>
        </g>
      );
    };
  })()
);

const GaugeTicksMask = React.memo(
  (() => {
    const ticks = macroScoreRanges
      .map(([low]) => (low / maxMacroScore) * 100)
      .map(adjustPercentage)
      .reverse();
    return ({
      tick,
      ...props
    }: SVGAttributes<any> & { tick?: number | null }) => {
      return (
        <g {...props}>
          {ticks.slice(tick ? tick - 1 : 0).map((position, i) => (
            <rect
              key={i}
              x={(canvas - tickWidth) / 2}
              y={canvas - strokeWidth - 1}
              width={tickWidth}
              height={strokeWidth + 2}
              fill="black"
              style={{
                transform: `rotate(${(position * 360) / 100}deg)`,
                transformOrigin: `center`,
              }}
            />
          ))}
        </g>
      );
    };
  })()
);

interface GaugeProps {
  size: number;
  score?: number | null;
  grade?: string;
  gradeColor?: string;
}

const Gauge = (props: GaugeProps) => {
  const { size, score: rawScore, grade, gradeColor } = props;
  const score = rawScore || 0;
  const tick = calculateTickByMacroScore(score);

  const [randomId] = useState(Math.random().toFixed(8).substr(2));
  const getId = (id: string) => `gauge-${randomId}-${id}`;

  const center = canvas / 2;
  const radius = (canvas - strokeWidth) / 2;
  const transition = "all 0.6s ease";
  const ellipseProps = { cx: center, cy: center, rx: radius, ry: radius };
  const ellipseCommon = {
    strokeWidth,
    stroke: "white",
    strokeLinecap: "round" as const,
    transformOrigin: "center",
    transition,
  };
  return (
    <SC.Container>
      <SC.ValueSelector
        style={{ transition }}
        canvas={canvas}
        size={size}
        strokeWidth={strokeWidth}
        progress={adjustPercentage((score / maxMacroScore) * 100)}
      />
      <svg height={size} width={size} viewBox={`0 0 ${canvas} ${canvas}`}>
        <rect
          height={canvas}
          width={canvas}
          fill={"#e0e7ef"}
          mask={`url(#${getId("gauge")})`}
        />
        <GaugeSvgGradient mask={`url(#${getId("progress")})`} />

        <foreignObject width={canvas} height={canvas}>
          <SC.ValueWrapper color={gradeColor} noHover={rawScore === null}>
            <SC.ScoreCalculated>
              {rawScore === null ? "?" : ~~score}
            </SC.ScoreCalculated>
            <SC.Grade>
              Grade
              <strong>{grade}</strong>
            </SC.Grade>
          </SC.ValueWrapper>
        </foreignObject>

        <defs>
          <mask id={getId("gauge")}>
            <ellipse
              {...ellipseProps}
              style={{
                ...ellipseCommon,
                strokeDasharray: radius * Math.PI * 2,
                strokeDashoffset:
                  radius * Math.PI * (((360 - strokeDegrees) / 360) * 2),
                transform: `rotate(${90 + (360 - strokeDegrees) / 2}deg)`,
              }}
            />
            <GaugeTicksMask tick={tick} />
          </mask>
          <mask id={getId("progress")}>
            <ellipse
              {...ellipseProps}
              style={{
                ...ellipseCommon,
                strokeDasharray: radius * Math.PI * 2,
                strokeDashoffset:
                  radius *
                  Math.PI *
                  (((360 - strokeDegrees * Math.min(score / maxMacroScore, 1)) /
                    360) *
                    2),
                transform: `rotate(${90 + (360 - strokeDegrees) / 2}deg)`,
              }}
            />
            <GaugeTicksMask />
            <ellipse
              cx={canvas / 2}
              cy={canvas - strokeSelector / 2}
              rx={strokeSelector / 2}
              ry={strokeSelector / 2}
              fill="white"
              style={{
                transition,
                transformOrigin: "center",
                transform: `rotate(${
                  (adjustPercentage((score / maxMacroScore) * 100) / 100) *
                    360 +
                  4
                }deg)`,
              }}
            />
          </mask>
        </defs>
      </svg>
    </SC.Container>
  );
};

// export default React.memo(Gauge)
export default Gauge;
